import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import PaymentCard from '../components/PaymentCard';
import ROYAPOSDevice from '../components/ROYAPOSDevice';
import { QrCode, CreditCard, Wallet, Zap, Globe, Shield, ArrowRight, Smartphone, TrendingUp, Briefcase, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const { t, direction } = useLanguage();

  const services = [
    { icon: QrCode, title: t('home.services.qrPayments'), desc: t('home.services.qrDesc') },
    { icon: CreditCard, title: t('home.services.posDevices'), desc: t('home.services.posDesc') },
    { icon: Wallet, title: t('home.services.digitalWallet'), desc: t('home.services.walletDesc') },
    { icon: Zap, title: t('home.services.instantTransfers'), desc: t('home.services.transferDesc') },
    { icon: Smartphone, title: t('home.services.paymentGateway'), desc: t('home.services.gatewayDesc') },
    { icon: CreditCard, title: t('home.services.virtualCards'), desc: t('home.services.virtualDesc') },
    { icon: CreditCard, title: t('home.services.physicalCards'), desc: t('home.services.physicalDesc') },
    { icon: TrendingUp, title: t('home.services.loans'), desc: t('home.services.loansDesc'), badge: 'Coming Soon' },
  ];

  const features = [
    { icon: QrCode, title: t('home.features.scan'), desc: t('home.features.scanDesc') },
    { icon: CreditCard, title: t('home.features.pos'), desc: t('home.features.posDesc') },
    { icon: Wallet, title: t('home.features.ecosystem'), desc: t('home.features.ecosystemDesc') },
    { icon: Zap, title: t('home.features.instant'), desc: t('home.features.instantDesc') },
    { icon: Globe, title: t('home.features.multilang'), desc: t('home.features.multilangDesc') },
    { icon: Shield, title: t('home.features.secure'), desc: t('home.features.secureDesc') },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-green-50/30 to-yellow-50/40"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-green-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl animate-pulse delay-700"></div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-8 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 rounded-full text-white font-semibold text-sm shadow-lg shadow-green-500/30 animate-fade-in">
                  <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                  Built for Vision 2030
                </div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 via-green-800 to-green-900 bg-clip-text text-transparent">
                    {t('home.heroTitle')}
                  </span>
                </h1>
                
                <p className="text-xl md:text-2xl text-gray-600 font-medium leading-relaxed">
                  {t('home.heroSubtitle')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-center md:justify-start pt-4">
                  <div className="text-center">
                    <p className="text-3xl md:text-4xl font-bold text-green-600">5M+</p>
                    <p className="text-sm text-gray-600 mt-1">Transactions</p>
                  </div>
                  <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
                  <div className="text-center">
                    <p className="text-3xl md:text-4xl font-bold text-green-600">50K+</p>
                    <p className="text-sm text-gray-600 mt-1">Merchants</p>
                  </div>
                  <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
                  <div className="text-center">
                    <p className="text-3xl md:text-4xl font-bold text-green-600">24/7</p>
                    <p className="text-sm text-gray-600 mt-1">Support</p>
                  </div>
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link to="/contact" className="group">
                    <button className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold text-lg shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105">
                      {t('home.getStarted')}
                      <ArrowRight className={`h-5 w-5 group-hover:translate-x-1 transition-transform ${direction === 'rtl' ? 'rotate-180' : ''}`} />
                    </button>
                  </Link>
                  <Link to="/about">
                    <button className="w-full sm:w-auto px-8 py-4 bg-white text-green-700 rounded-xl font-semibold text-lg border-2 border-green-600 hover:bg-green-50 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105">
                      {t('home.learnMore')}
                    </button>
                  </Link>
                </div>
              </div>
              
              {/* Right Content - Card */}
              <div className="flex justify-center">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-yellow-500 rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
                  <div className="relative transform hover:scale-105 transition-transform duration-500">
                    <PaymentCard />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-4 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4">
          <p className="text-center text-white text-lg md:text-xl font-semibold">
            One ecosystem. One platform. One financial experience.
          </p>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-white to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div className="order-2 lg:order-1">
                <div className="inline-block px-4 py-2 bg-green-100 rounded-full text-green-700 font-medium text-sm mb-6">
                  Mobile App
                </div>
                <h2 className="heading-2 mb-6">Your Complete Financial Hub in Your Pocket</h2>
                <p className="text-lg text-gray-700 mb-8">
                  Manage your money, track your business, and grow your wealth with the ROYA mobile app. 
                  Everything you need for smart financial management in one beautiful interface.
                </p>

                <div className="space-y-4 mb-8">
                  {[
                    { title: 'Real-time Balance', desc: 'Track your available balance instantly' },
                    { title: 'Quick Actions', desc: 'Top-up, transfer, request payments with one tap' },
                    { title: 'IBAN Integration', desc: 'Receive payments directly to your ROYA account' },
                    { title: 'Business Analytics', desc: 'Monitor your sales and transactions' },
                    { title: 'Multi-Service Access', desc: 'POS devices, cards, and payment gateway' },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                        style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">{feature.title}</h4>
                        <p className="text-sm text-gray-600">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button className="btn-primary">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" className="mr-2">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Download on iOS
                  </Button>
                  <Button className="btn-secondary">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    Get it on Android
                  </Button>
                </div>
              </div>

              {/* App Mockup */}
              <div className="order-1 lg:order-2 flex justify-center">
                <div
                  className="relative"
                  style={{
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                    transition: 'transform 0.3s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'perspective(1000px) rotateY(-5deg) rotateX(2deg)'}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/300htpd4_front%20roya.jpeg"
                    alt="ROYA Mobile App"
                    className="w-full max-w-sm rounded-3xl shadow-2xl"
                    style={{
                      boxShadow: '0 25px 50px rgba(16, 185, 129, 0.4), 0 0 100px rgba(16, 185, 129, 0.2)',
                    }}
                  />
                  
                  {/* Decorative elements */}
                  <div
                    className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full opacity-20 blur-2xl"
                  />
                  <div
                    className="absolute -bottom-4 -left-4 w-32 h-32 bg-green-400 rounded-full opacity-20 blur-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Payment Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div>
                <div className="inline-block px-4 py-2 bg-green-100 rounded-full text-green-700 font-medium text-sm mb-6">
                  QR Payments
                </div>
                <h2 className="heading-2 mb-6">Scan & Pay</h2>
                <p className="text-xl text-gray-700 mb-4 font-medium" style={{ color: '#1B9E4B' }}>
                  Enjoy instant, secure, and seamless QR payments with ROYA
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  No more cash handling or card swiping. Simply scan the QR code and complete your payment instantly. 
                  Perfect for restaurants, cafes, retail stores, and any business that wants to offer modern payment solutions.
                </p>

                <div className="space-y-4 mb-8">
                  {[
                    { 
                      icon: Zap,
                      title: 'Instant Payments', 
                      desc: 'Complete transactions in seconds with QR code scanning' 
                    },
                    { 
                      icon: Shield,
                      title: 'Secure Transactions', 
                      desc: 'Bank-grade security for every payment' 
                    },
                    { 
                      icon: Smartphone,
                      title: 'No Hardware Required', 
                      desc: 'Just print a QR code and start accepting payments' 
                    },
                    { 
                      icon: TrendingUp,
                      title: 'Real-time Notifications', 
                      desc: 'Get instant alerts for every successful payment' 
                    },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #1B9E4B 0%, #159642 100%)' }}
                      >
                        <feature.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">{feature.title}</h4>
                        <p className="text-sm text-gray-600">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button className="btn-primary">
                  <QrCode className="h-5 w-5" />
                  Start Using QR Payments
                </Button>
              </div>

              {/* Image */}
              <div className="relative">
                <div
                  className="relative rounded-3xl overflow-hidden shadow-2xl"
                  style={{
                    boxShadow: '0 25px 50px rgba(27, 158, 75, 0.3)',
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/pw76rdrl_WhatsApp%20Image%201447-05-27%20at%2007.16.10.jpeg"
                    alt="ROYA QR Payment"
                    className="w-full h-auto"
                  />
                  
                  {/* Overlay gradient for better visibility */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to bottom, transparent 0%, rgba(27, 158, 75, 0.05) 100%)',
                    }}
                  />
                </div>

                {/* Decorative elements */}
                <div
                  className="absolute -top-6 -right-6 w-32 h-32 bg-yellow-400 rounded-full opacity-20 blur-3xl"
                />
                <div
                  className="absolute -bottom-6 -left-6 w-32 h-32 bg-green-400 rounded-full opacity-20 blur-3xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apple Pay & Mada Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-white to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-green-600 to-green-700 p-1">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-6">
                    <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                      Payment Partners
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                      Pay with Apple Pay & Mada
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Add your Roya Card to Apple Pay and use Mada for seamless payments with one tap — anywhere, anytime.
                    </p>
                    <p className="text-base text-gray-600 leading-relaxed" dir="rtl">
                      ضيف بطاقة روية على ابل باي واستخدم مدى للدفع بلمسة وحدة في اي مكان
                    </p>
                    
                    <div className="pt-4">
                      <Link to="/contact">
                        <button className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center gap-2">
                          Get Started
                          <ArrowRight className="h-5 w-5" />
                        </button>
                      </Link>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-4">
                    <div className="bg-gradient-to-br from-gray-900 to-black text-white px-8 py-6 rounded-2xl font-semibold flex items-center gap-3 shadow-2xl transform hover:scale-105 transition-transform">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      <span className="text-xl">Apple Pay</span>
                    </div>
                    
                    <div className="bg-white border-3 border-gray-300 px-8 py-6 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform">
                      <img
                        src="https://customer-assets.emergentagent.com/job_royapay/artifacts/uqwqhp4w_WhatsApp%20Image%201447-06-18%20at%2009.26.11.jpeg"
                        alt="Mada"
                        className="h-10 object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
              Our Services
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('home.featuresTitle')}</h2>
            <p className="text-xl text-green-600 font-medium mb-2">
              Smart finance for a smart nation
            </p>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Complete fintech solutions for merchants and individuals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-2xl p-6 border border-gray-200 hover:border-green-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                {service.badge && (
                  <div className="absolute top-4 right-4">
                    <span className="text-xs px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold">
                      {service.badge}
                    </span>
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-500/30">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-green-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.desc}
                </p>
                
                {/* Hover indicator */}
                <div className="mt-4 flex items-center text-green-600 opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">
                  Learn more <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20" style={{ background: 'linear-gradient(to bottom, #ffffff 0%, #f0fdf4 100%)' }}>
        <div className="container mx-auto px-4">
          <h2 className="heading-2 text-center mb-12">Why Choose ROYA</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <div
                  className="p-4 rounded-full mb-4"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  }}
                >
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-yellow-50 to-green-50">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-green-100 rounded-full text-green-700 font-medium text-sm mb-6">
              Vision 2030
            </div>
            <h2 className="heading-2 mb-6">The Future of Payments Starts Here</h2>
            <p className="text-xl font-semibold mb-4" style={{ color: '#1B9E4B' }}>
              Roya Business is built for the future of financial services in Saudi Arabia — aligned with Vision 2030 and the Kingdom&apos;s digital transformation strategy.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Roya is not just an app, but a unified financial ecosystem designed to simplify every part of personal and business payments.
            </p>

            <div className="flex justify-center items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/1tnawu9j_riyal%20logo.jpeg"
                alt="Saudi Riyal"
                className="w-12 h-12"
              />
              <div className="text-left">
                <p className="font-bold text-xl" style={{ color: '#10B981' }}>Saudi Financial Identity</p>
                <p className="text-sm text-gray-600">Supporting the future of Saudi fintech</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="heading-2 mb-6">Ready to Grow Together?</h2>
            <p className="text-lg text-gray-600 mb-4">
              Join thousands of merchants and individuals across Saudi Arabia using ROYA for smart payments
            </p>
            <p className="text-base text-gray-500 italic mb-8">
              ROYA — your trusted Saudi payment partner, built for your business, your family, and your future.
            </p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-6">
                {t('home.getStarted')}
                <ArrowRight className={`h-5 w-5 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
